import React from 'react';
import type { FC } from 'react';

interface ResizeHandleProps {
    position: 'top' | 'bottom';
    visible: boolean;
    onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    className?: string;
    /** Avoid opacity/transform transitions — Safari composites badly on grid‑placed parents */
    reduceLayerMotion?: boolean;
}

/**
 * ResizeHandle component for schedule cards
 * Provides visual handles for resizing schedule entries by dragging top or bottom edges
 */
const ResizeHandle: FC<ResizeHandleProps> = ({
    position,
    visible,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    className = '',
    reduceLayerMotion = false
}) => {
    if (!visible) return null;

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onMouseDown(e);
    };

    const baseClasses = reduceLayerMotion
        ? `
        absolute left-0 right-0 z-20
        bg-transparent hover:bg-blue-500/25
        cursor-ns-resize
        flex items-center justify-center
        opacity-40 hover:opacity-100
        group-hover:opacity-55
        `
        : `
        absolute left-0 right-0 z-20 
        bg-transparent hover:bg-blue-500/20 
        transition-all duration-150 ease-in-out
        cursor-ns-resize
        flex items-center justify-center
        opacity-0 hover:opacity-100
        group-hover:opacity-60 hover:!opacity-100
    `;

    const positionClasses = position === 'top'
        ? reduceLayerMotion ? 'top-0 h-3' : 'top-0 -mt-1 h-3'
        : reduceLayerMotion ? 'bottom-0 h-3 pb-px' : 'bottom-0 -mb-1 h-3';

    const knobClass = reduceLayerMotion
        ? 'w-8 h-0.5 bg-blue-500 rounded-full opacity-0 hover:opacity-100'
        : 'w-8 h-0.5 bg-blue-500 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-150';

    return (
        <div
            className={`${baseClasses} ${positionClasses} ${className}`}
            onMouseDown={handleMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            data-resize-handle={position}
        >
            {/* Visual indicator - only show on hover */}
            <div className={knobClass} />
        </div>
    );
};

export default ResizeHandle;